import DeviceCalls from '../api/DeviceCalls';
import { LABEL_ALREADY_ADDED, LABEL_NEW } from '../api/Constants';
import { DeviceDataAPIResponse } from '../api/device.model';
import { AppDispatch, AppStore, RootState } from '../../store.model';
import { getDeviceDataById, isDeviceInAssociatedDevices } from '../selectors';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { nameReducer } from '../device.model';
import { actionsDevices } from '../slice/devicesSlice';
import { ErrorRestST } from '../../../axios/rest.model';
import { IconUpload } from '../../groups/groups.model';
import { chunk } from 'lodash';
import fetchOwnRights from '../../rights/actions';
import { CommanderAPI } from '../api/CommanderAPI';

// ------------------------------------
// Thunks created with @reduxjs/toolkit
// ------------------------------------

export const fetchedDeviceData = createAsyncThunk(
  `${nameReducer}/fetchedDeviceData`,
  async (promiseArg: Promise<DeviceDataAPIResponse>) => {
    return await promiseArg;
  },
  // It is necessary because front need ErrorRestST['errorId']
  { serializeError: (e: unknown) => e as ErrorRestST }
);

export const fetchedDevicesData = createAsyncThunk(
  `${nameReducer}/fetchedDevicesData`,
  async (promiseArg: Promise<DeviceDataAPIResponse[]>) => {
    return await promiseArg;
  },
  // It is necessary because front need ErrorRestST['errorId']
  { serializeError: (e: unknown) => e as ErrorRestST }
);

export const loadDevicesAssociated = createAsyncThunk<
  any,
  undefined,
  { rejectValue: ErrorRestST; state: RootState; dispatch: AppDispatch }
>(
  `${nameReducer}/loadDevicesAssociated`,
  async (_, thunkAPI) => {
    return await thunkAPI.dispatch(fetchDevicesAssociated());
  },
  // It is necessary because front need ErrorRestST['errorId']
  { serializeError: (e: unknown) => e as ErrorRestST }
);

// --------------
// Simple thunks
// --------------
export const fetchDevicesByIds =
  (devicesIds: string[]) => async (dispatch: AppDispatch) => {
    return Promise.all([dispatch(fetchedDevicesDataInChunks(devicesIds))]);
  };

/**
 * fetchedDevicesDataInChunks split the calls per 50 deviceIds per call,
 * because the call's headers would be too large if we use >100 deviceIds per call
 *
 * @param devicesIds
 * @returns chunks
 */
export const fetchedDevicesDataInChunks =
  (devicesIds: string[]) => async (dispatch: AppDispatch) => {
    const deviceCalls = new DeviceCalls();
    const chunksDeviceIds: string[][] = chunk(devicesIds, 50);
    return Promise.all(
      chunksDeviceIds.map((chunkDeviceIds) =>
        dispatch(
          fetchedDevicesData(deviceCalls.getDevicesDataByIds(chunkDeviceIds))
        )
      )
    );
  };

export const fetchDevicesAssociated = () => async (dispatch: AppDispatch) => {
  const deviceCalls = new DeviceCalls();
  const deviceDataPromise = deviceCalls.getAssociatedDevices();
  await dispatch(fetchedDevicesData(deviceDataPromise));

  // Don't wait all the calls to show myDevices
  return deviceDataPromise.then((devicesData) => {
    const devicesIds = devicesData.map((deviceData) => deviceData.uuid);
    return dispatch(actionsDevices.associateDevices(devicesIds));
  });
};

/**
 * Fetch device by cloud connect id
 * @param string cloudConnectId
 */
export const fetchDeviceByCloudConnectId = async (cloudConnectId: string) => {
  const deviceCalls = new DeviceCalls();
  return await deviceCalls.getDeviceByCloudConnectId(cloudConnectId);
};

export const postAssociatedDevice =
  (cloudConnectId: string) =>
  async (dispatch: AppDispatch, getState: AppStore['getState']) => {
    const deviceCalls = new DeviceCalls();
    await deviceCalls.postAssociatedDevice(cloudConnectId);
    const deviceData = await deviceCalls.getDeviceByCloudConnectId(
      cloudConnectId
    );
    if (
      isDeviceInAssociatedDevices(getState(), { deviceId: deviceData.uuid })
    ) {
      dispatch(
        actionsDevices.addLabel({
          deviceId: deviceData.uuid,
          label: LABEL_ALREADY_ADDED,
        })
      );
    } else {
      // load & add to list
      await dispatch(
        CommanderAPI.util.invalidateTags(['commander-associated'])
      );
      await dispatch(
        CommanderAPI.endpoints.getAssociatedDevices.initiate(undefined, {
          forceRefetch: true,
        })
      ).unwrap();
      await dispatch(fetchOwnRights());
      dispatch(
        actionsDevices.addLabel({
          deviceId: deviceData.uuid,
          label: LABEL_NEW,
        })
      );
    }
  };

export const postAssociatedDeviceWithSerialNumber =
  (serialNumber: string) =>
  async (dispatch: AppDispatch, getState: AppStore['getState']) => {
    const deviceCalls = new DeviceCalls();
    await deviceCalls.postAssociatedDeviceWithSerialNumber(serialNumber);
    const deviceData = await deviceCalls.getDeviceBySerialNumber(serialNumber);
    if (
      isDeviceInAssociatedDevices(getState(), { deviceId: deviceData.uuid })
    ) {
      dispatch(
        actionsDevices.addLabel({
          deviceId: deviceData.uuid,
          label: LABEL_ALREADY_ADDED,
        })
      );
    } else {
      // load & add to list
      await dispatch(
        CommanderAPI.util.invalidateTags(['commander-associated'])
      );
      await dispatch(
        CommanderAPI.endpoints.getAssociatedDevices.initiate(undefined, {
          forceRefetch: true,
        })
      ).unwrap();
      await dispatch(fetchOwnRights());
      dispatch(
        actionsDevices.addLabel({
          deviceId: deviceData.uuid,
          label: LABEL_NEW,
        })
      );
    }
  };

export const putDevice =
  (
    deviceId: string,
    deviceData: DeviceDataAPIResponse,
    iconUpload?: IconUpload
  ) =>
  async (dispatch: AppDispatch) => {
    const deviceCalls = new DeviceCalls();

    const deviceDataResponse = await deviceCalls.putDevice(
      deviceId,
      deviceData,
      iconUpload
    );
    // invalidate commander-data CommanderAPI
    await dispatch(CommanderAPI.util.invalidateTags(['commander-data']));
    dispatch(actionsDevices.updateDevice(deviceDataResponse));
  };

/**
 * Delete device icon
 * @param class restClient
 * @param class polyglot
 * @param string deviceId
 */
export const deleteDeviceIcon =
  (deviceId: string) => async (dispatch: AppDispatch) => {
    const deviceCalls = new DeviceCalls();
    await deviceCalls.deleteIcon(deviceId);
    await dispatch(CommanderAPI.util.invalidateTags(['commander-data']));
  };

/**
 * Get device by device id
 * this function tries to get the device from the groups structure, if this is not
 * found then a call will be done to get the device from the service
 * @param class restClient
 * @param class polyglot
 * @param string deviceId
 */
export const getDeviceByDeviceId =
  (deviceId: string) =>
  async (dispatch: AppDispatch, getState: AppStore['getState']) => {
    const deviceData = getDeviceDataById(getState(), { deviceId });
    /* if the device is not found in the groups array then it will ask for this device */
    if (!deviceData) {
      await dispatch(fetchDevicesByIds([deviceId]));
    }
  };
